import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useFileSystem, setAccountType } from './hooks/useFileSystem';
import { DEFAULT_ACCOUNTS } from './constants/defaultAccounts';
import { ACCOUNT_TYPES, inferAccountType } from './constants/accountTypes';
import { ORG_TYPES, ORG_TYPE_LABELS, isIndividual, isForProfit, getOrgInfo, saveOrgInfo, loadOrgFromApi } from './constants/orgTypes';
import { getCurrentFiscalYear } from './utils/date';
import { generateId } from './utils/formatting';
import { Sidebar } from './components/common';

// 家事按分自動生成仕訳の識別子
const AUTO_APPORTIONMENT_DESCRIPTION = '家事按分（自動生成）';

// Components - will be imported once extracted
import Dashboard from './components/reports/Dashboard';
import JournalEntry from './components/journal/JournalEntry';
import AccountMaster from './components/master/AccountMaster';
import GeneralLedger from './components/reports/GeneralLedger';
import TrialBalance from './components/reports/TrialBalance';
import FinancialStatements from './components/reports/FinancialStatements';
import TaxSummary from './components/tax/TaxSummary';
import FixedAssets from './components/assets/FixedAssets';
import ClosingWizard from './components/closing/ClosingWizard';
import Deductions from './components/closing/Deductions';
import ETaxTransfer from './components/tax/ETaxTransfer';
import CardSettlement from './components/journal/CardSettlement';
import DataManagement from './components/settings/DataManagement';
import Help from './components/help/Help';

// Mobile Components
import JournalList from './components/mobile/JournalList';
import CameraScreen from './components/mobile/CameraScreen';
import EntryForm from './components/mobile/EntryForm';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const RECOMMENDED_PATH = '/Users/a7010/Library/CloudStorage/GoogleDrive-admin@hanihohetoilo.org/マイドライブ/ハニホヘトイロ/会計/帳簿';

const App = () => {
    // URLから招待トークンを検出
    const [inviteToken] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('invite');
        if (token) {
            // URLからトークンパラメータを消す（リロード対策）
            window.history.replaceState({}, '', window.location.pathname);
        }
        return token;
    });

    // ユーザー認証状態
    const [user, setUser] = useState(() => {
        const stored = localStorage.getItem('haniho_user');
        return stored ? JSON.parse(stored) : null;
    });

    // Google OAuth ログイン処理
    const handleGoogleLogin = () => {
        if (!window.google?.accounts?.oauth2) {
            alert('Google認証の読み込みに失敗しました。ページを再読み込みしてください。');
            return;
        }
        const client = window.google.accounts.oauth2.initTokenClient({
            client_id: GOOGLE_CLIENT_ID,
            scope: 'email profile',
            callback: async (response) => {
                if (response.access_token) {
                    try {
                        const res = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
                            headers: { Authorization: `Bearer ${response.access_token}` }
                        });
                        const userInfo = await res.json();
                        const userData = {
                            email: userInfo.email,
                            name: userInfo.name,
                            picture: userInfo.picture,
                            accessToken: response.access_token
                        };
                        // setUserは全ての非同期処理が終わってから呼ぶ（useEffectのrace condition防止）
                        setIsLoginProcessing(true);
                        const API_URL = import.meta.env.VITE_API_URL;
                        try {
                            if (inviteToken) {
                                const acceptRes = await fetch(`${API_URL}?action=acceptInvite`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ token: inviteToken, email: userInfo.email })
                                });
                                const acceptData = await acceptRes.json();
                                if (acceptData.success) {
                                    const at = acceptData.orgInfo ? (isIndividual(acceptData.orgInfo.type) ? 'kojin' : 'houjin') : 'houjin';
                                    // localStorage を先にセットしてからstateを一括更新
                                    localStorage.setItem('haniho_user', JSON.stringify(userData));
                                    localStorage.setItem('toiro_active_book_email', acceptData.ownerEmail);
                                    localStorage.setItem('toiro_org_info', JSON.stringify(acceptData.orgInfo));
                                    localStorage.setItem('haniho_account_type', at);
                                    setUser(userData);
                                    setActiveBookEmail(acceptData.ownerEmail);
                                    setOrgInfo(acceptData.orgInfo);
                                    setAccountType(at);
                                    return;
                                } else {
                                    alert(acceptData.error || '招待リンクが無効です');
                                }
                            }

                            // 招待トークンなし or 失敗 → 通常のログインフロー
                            const booksRes = await fetch(`${API_URL}?action=getAccessibleBooks&user=${encodeURIComponent(userInfo.email)}`);
                            const booksData = await booksRes.json();
                            const books = booksData.books || [];

                            let bookEmail = userInfo.email;
                            if (books.length === 1) {
                                bookEmail = books[0].ownerEmail;
                            } else if (books.length > 1) {
                                bookEmail = null;
                            }

                            const loadEmail = bookEmail || userInfo.email;
                            const apiOrg = await loadOrgFromApi(loadEmail);
                            const accountType = isIndividual(apiOrg?.type) ? 'kojin' : 'houjin';

                            // localStorage を先にセットしてからstateを一括更新
                            localStorage.setItem('haniho_user', JSON.stringify(userData));
                            if (bookEmail) localStorage.setItem('toiro_active_book_email', bookEmail);
                            localStorage.setItem('toiro_org_info', JSON.stringify(apiOrg));
                            localStorage.setItem('haniho_account_type', accountType);

                            setUser(userData);
                            if (bookEmail) setActiveBookEmail(bookEmail);
                            setOrgInfo(apiOrg);
                            setAccountType(accountType);

                            if (books.length > 1) {
                                setAvailableBooks(books);
                                setShowBookSelector(true);
                            }
                        } catch (err) {
                            console.error('Failed to load org info:', err);
                            localStorage.setItem('haniho_user', JSON.stringify(userData));
                            localStorage.setItem('toiro_active_book_email', userInfo.email);
                            setUser(userData);
                            setActiveBookEmail(userInfo.email);
                            setOrgInfo(null);
                        } finally {
                            setIsLoginProcessing(false);
                        }
                    } catch (err) {
                        console.error('Failed to get user info:', err);
                        alert('ユーザー情報の取得に失敗しました');
                    }
                }
            }
        });
        client.requestAccessToken();
    };

    // ログアウト処理
    const handleLogout = () => {
        // Googleのオートサインインを無効化（リロードで再ログインされないよう）
        window.google?.accounts?.id?.disableAutoSelect();
        setUser(null);
        setActiveBookEmail(null);
        setAvailableBooks([]);
        setShowBookSelector(false);
        localStorage.removeItem('haniho_user');
        localStorage.removeItem('toiro_org_info');
        localStorage.removeItem('haniho_account_type');
        localStorage.removeItem('toiro_active_book_email');
        // 状態をリセット
        setJournals([]);
        setAccounts(DEFAULT_ACCOUNTS);
        setOrgInfo(null);
        setStartupStep('year');
    };

    // 状態管理
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [currentPage, setCurrentPage] = useState(isMobile ? 'mobile-journal-list' : 'dashboard');
    const [mobileScreen, setMobileScreen] = useState('list'); // 'list', 'camera', 'entry'
    const [ocrData, setOcrData] = useState(null);
    const [isOcrProcessing, setIsOcrProcessing] = useState(false);
    // fiscalYears: [{year: 2024, endMonth: 3}, ...] の形式
    const [fiscalYears, setFiscalYears] = useState([]);
    const [currentFiscalYear, setCurrentFiscalYear] = useState(getCurrentFiscalYear());
    const [accounts, setAccounts] = useState(DEFAULT_ACCOUNTS);
    const [journals, setJournals] = useState([]);
    const [alertState, setAlertState] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [startupStep, setStartupStep] = useState('year'); // 'folder', 'year', 'done' (Google Drive連携のためyearから開始)
    const [selectedStartupYear, setSelectedStartupYear] = useState(getCurrentFiscalYear());
    const [isLoadingYears, setIsLoadingYears] = useState(true); // 年度リスト読み込み中
    const [focusJournalId, setFocusJournalId] = useState(null); // 元帳からのジャンプ用
    const [deletedJournals, setDeletedJournals] = useState([]); // 削除済み仕訳（電子帳簿保存法対応）
    const [newAccountsAlert, setNewAccountsAlert] = useState(null); // 新規追加された科目の通知
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // モバイルメニューの開閉状態
    const [creditCards, setCreditCards] = useState([]);
    const [detailSettings, setDetailSettings] = useState({
        bankAccounts: [],
        partners: [],
        officers: [],
        rentals: []
    });
    const [allocationSettings, setAllocationSettings] = useState({
        method: 'revenue',
        revenueRatio: 50,
        nonRevenueRatio: 50,
        customRatios: {}
    });
    const [orgInfo, setOrgInfo] = useState(() => {
        // 初期値をlocalStorageから読み込み（ちらつき防止）
        const saved = localStorage.getItem('toiro_org_info');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (e) {
                return null;
            }
        }
        return null;
    }); // 組織情報（APIから読み込み）
    const [isLoginProcessing, setIsLoginProcessing] = useState(false);
    const [isLoadingOrgInfo, setIsLoadingOrgInfo] = useState(() => {
        // localStorageにorgInfoがなく、userがいる場合は読み込み中
        const saved = localStorage.getItem('toiro_org_info');
        const storedUser = localStorage.getItem('haniho_user');
        return !saved && !!storedUser;
    });

    // 年度選択画面用のstate（条件分岐の外に移動）
    const [showNewYearModal, setShowNewYearModal] = useState(false);
    const [newYearInput, setNewYearInput] = useState(() => {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;
        // 初期値はhoujin（法人）基準。組織情報読み込み後に更新される
        return currentMonth >= 4 ? currentYear : currentYear - 1;
    });
    const [newEndMonth, setNewEndMonth] = useState(() => {
        // 前年度の決算月があればそれを使う（なければ3月）
        const saved = localStorage.getItem('haniho_last_end_month');
        return saved ? parseInt(saved) : 3;
    });
    const [isCreating, setIsCreating] = useState(false);

    // 複数人共同作業: 現在開いている帳簿のオーナーメール
    const [activeBookEmail, setActiveBookEmail] = useState(() => {
        const stored = localStorage.getItem('toiro_active_book_email');
        return stored || null;
    });
    const [availableBooks, setAvailableBooks] = useState([]);
    const [showBookSelector, setShowBookSelector] = useState(false);
    const isBookOwner = !activeBookEmail || activeBookEmail === user?.email;

    const fileSystem = useFileSystem(activeBookEmail || user?.email);
    const saveTimeoutRef = useRef(null);

    // localStorageから組織情報と会計種別を取得
    const storedOrgInfo = getOrgInfo();
    const storedAccountType = localStorage.getItem('haniho_account_type');

    // ユーザーがログイン済みで、localStorageに組織情報がない場合、APIから読み込み
    useEffect(() => {
        const storedOrg = localStorage.getItem('toiro_org_info');
        const effectiveEmail = activeBookEmail || user?.email;
        if (user && effectiveEmail && !storedOrg) {
            setIsLoadingOrgInfo(true);
            loadOrgFromApi(effectiveEmail)
                .then(apiOrg => {
                    setOrgInfo(apiOrg);
                    const accountType = isIndividual(apiOrg?.type) ? 'kojin' : 'houjin';
                    localStorage.setItem('toiro_org_info', JSON.stringify(apiOrg));
                    localStorage.setItem('haniho_account_type', accountType);
                    setAccountType(accountType);
                })
                .catch(err => {
                    console.log('No org info found for user, will show setup screen');
                    setOrgInfo(null);
                })
                .finally(() => {
                    setIsLoadingOrgInfo(false);
                });
        } else {
            setIsLoadingOrgInfo(false);
        }
    }, [user, activeBookEmail]);

    // 帳簿選択ハンドラー
    const handleBookSelect = async (book) => {
        setActiveBookEmail(book.ownerEmail);
        localStorage.setItem('toiro_active_book_email', book.ownerEmail);
        setShowBookSelector(false);
        // 選択した帳簿のorgInfoを読み込み
        try {
            const apiOrg = await loadOrgFromApi(book.ownerEmail);
            setOrgInfo(apiOrg);
            const accountType = isIndividual(apiOrg?.type) ? 'kojin' : 'houjin';
            localStorage.setItem('toiro_org_info', JSON.stringify(apiOrg));
            localStorage.setItem('haniho_account_type', accountType);
            setAccountType(accountType);
        } catch (err) {
            console.error('Failed to load org info for selected book:', err);
        }
    };

    // ドキュメントタイトルとaccountTypeを更新
    useEffect(() => {
        const isKojin = orgInfo && isIndividual(orgInfo.type);
        if (isKojin) {
            document.title = 'トイロ青色申告';
        } else if (orgInfo?.name) {
            document.title = `${orgInfo.name} - トイロ法人会計`;
        } else {
            document.title = 'トイロ法人会計';
        }
        // useFileSystemのaccountTypeも更新（API呼び出し時に正しいtypeを使用するため）
        setAccountType(isKojin ? 'kojin' : 'houjin');
    }, [orgInfo]);

    // 画面サイズ変更の監視
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile && currentPage !== 'mobile-journal-list' && !['list', 'camera', 'entry'].includes(currentPage)) {
                setCurrentPage('mobile-journal-list');
                setMobileScreen('list');
            } else if (!mobile && currentPage === 'mobile-journal-list') {
                setCurrentPage('dashboard');
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [currentPage]);

    // ブラウザの戻る/進むボタン対応
    const navigateTo = useCallback((page) => {
        setCurrentPage(page);
        window.history.pushState({ page }, '');
    }, []);

    useEffect(() => {
        const onPopState = (e) => {
            const page = e.state?.page;
            if (page) setCurrentPage(page);
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    // アラート表示
    const showAlert = (message, type = 'success') => {
        setAlertState({ message, type });
        setTimeout(() => setAlertState(null), 3000);
    };

    // データを読み込み（年度データの科目を使用、なければDEFAULT_ACCOUNTS）
    const loadYearData = async (year) => {
        if (!fileSystem.isConnected) {
            showAlert('APIに接続できません。ネットワーク接続を確認してください。', 'error');
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        try {
            // 年度データを読み込み（科目はDEFAULT_ACCOUNTSをフォールバックに使用）
            const data = await fileSystem.loadData(year, DEFAULT_ACCOUNTS);
            if (!data) {
                throw new Error('データの読み込みに失敗しました');
            }

            // data.accountsには新規科目が既に含まれているのでそれを使用
            const finalAccounts = data.accounts || accountsToUse;

            // BUG-4修正: 仕訳の科目IDをサニタイズ（ID不整合の救済）
            const sanitizeJournals = (journals, accounts) => {
                return journals.map(j => ({
                    ...j,
                    lines: j.lines.map(line => {
                        // accountIdで科目が見つかればOK
                        const foundById = accounts.find(a => a.id === line.accountId);
                        if (foundById) return line;

                        // 見つからない場合、accountIdをそのまま科目名として扱って検索（後方互換）
                        // または、debit_account/credit_accountフィールドがあればそれを使用
                        console.warn(`科目ID不一致: accountId=${line.accountId}を名前で再マッピング試行`);

                        // accountIdが実は科目名である可能性（旧データ形式の互換性）
                        const foundByName = accounts.find(a => a.name === line.accountId);
                        if (foundByName) {
                            console.log(`  → 科目名 "${line.accountId}" で見つかりました。IDを ${foundByName.id} に更新`);
                            return { ...line, accountId: foundByName.id };
                        }

                        // それでも見つからない場合はそのまま（ダッシュボードでは集計されない）
                        console.warn(`  → 科目が見つかりません: ${line.accountId}`);
                        return line;
                    })
                }));
            };

            const sanitizedJournals = sanitizeJournals(data.journals || [], finalAccounts);
            const sanitizedDeletedJournals = sanitizeJournals(data.deletedJournals || [], finalAccounts);

            setAccounts(finalAccounts);
            setJournals(sanitizedJournals);
            setDeletedJournals(sanitizedDeletedJournals);

            // 新規科目があれば通知
            if (data?.newAccounts && data.newAccounts.length > 0) {
                setNewAccountsAlert({
                    count: data.newAccounts.length,
                    names: data.newAccounts.map(a => a.name)
                });
            }

            // 家事按分の自動仕訳生成（個人事業主のみ）
            const currentOrgInfo = getOrgInfo();
            if (isIndividual(currentOrgInfo?.type)) {
                await generateApportionmentEntries(year, sanitizedJournals, finalAccounts);
            }
        } catch (err) {
            console.error('Failed to load data:', err);
            showAlert('データの読み込みに失敗しました', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // 家事按分の自動仕訳生成
    const generateApportionmentEntries = async (year, loadedJournals, loadedAccounts) => {
        try {
            // 条件1: 期末日（12/31）より後かどうか
            const now = new Date();
            const currentYear = now.getFullYear();
            if (year >= currentYear) {
                return;
            }

            // 条件2: 仕訳が存在するか
            if (!loadedJournals || loadedJournals.length === 0) {
                return;
            }

            // 条件3: 既存の家事按分仕訳があるか確認
            const existingEntries = loadedJournals.filter(j =>
                j.closingType === 'personal-use' ||
                j.description?.includes(AUTO_APPORTIONMENT_DESCRIPTION)
            );
            if (existingEntries.length > 0) {
                return;
            }

            // 按分設定を取得
            let apportionmentData = await fileSystem.getApportionment(year);

            // 設定が空で前年度設定がある場合は自動コピー
            if (!apportionmentData || !apportionmentData.ratios || Object.keys(apportionmentData.ratios).length === 0) {
                const prevYear = year - 1;
                const prevData = await fileSystem.getApportionment(prevYear);
                if (prevData && prevData.ratios && Object.keys(prevData.ratios).length > 0) {
                    const copiedData = {
                        ...prevData,
                        updatedAt: new Date().toISOString()
                    };
                    await fileSystem.saveApportionment(year, copiedData);
                    apportionmentData = copiedData;
                }
            }

            if (!apportionmentData || !apportionmentData.ratios) {
                return;
            }

            // 按分設定を抽出
            const ratios = {};
            Object.entries(apportionmentData.ratios).forEach(([accountId, setting]) => {
                const br = Number(setting.businessRatio);
                if (!isNaN(br) && br > 0 && br < 100) {
                    ratios[accountId] = br;
                }
            });

            if (Object.keys(ratios).length === 0) {
                return;
            }

            // 事業主貸科目を取得
            const personalDrawingAccount = loadedAccounts.find(a => a.name === '事業主貸');
            if (!personalDrawingAccount) {
                console.error('App: 事業主貸科目が見つかりません');
                return;
            }

            // 経費合計を計算するヘルパー
            const getExpenseTotal = (accountId) => {
                return loadedJournals
                    .filter(j => j.closingType !== 'personal-use')
                    .flatMap(j => j.lines || [])
                    .filter(l => l.accountId === accountId)
                    .reduce((sum, l) => sum + (l.type === 'debit' ? l.amount : -l.amount), 0);
            };

            // 自動生成仕訳を作成
            const entries = [];
            Object.entries(ratios).forEach(([accountId, ratio]) => {
                if (!ratio || ratio <= 0 || ratio >= 100) return;

                const total = getExpenseTotal(accountId);
                const personalAmount = Math.floor(total * (100 - ratio) / 100);
                if (personalAmount <= 0) return;

                const account = loadedAccounts.find(a => a.id === accountId);
                entries.push({
                    id: generateId(),
                    date: `${year}-12-31`,
                    description: `${AUTO_APPORTIONMENT_DESCRIPTION} ${account?.name || ''}`,
                    lines: [
                        { id: generateId(), type: 'debit', accountId: personalDrawingAccount.id, amount: personalAmount },
                        { id: generateId(), type: 'credit', accountId, amount: personalAmount }
                    ],
                    isClosingEntry: true,
                    closingType: 'personal-use',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                });
            });

            if (entries.length > 0) {
                setJournals(prev => [...prev, ...entries]);
                showAlert(`家事按分の決算整理仕訳を自動生成しました（${entries.length}件）`);
            }
        } catch (err) {
            console.error('Failed to generate apportionment entries:', err);
        }
    };

    // データを保存
    const saveData = async () => {
        const data = {
            fiscalYear: currentFiscalYear,
            accounts,
            journals,
            deletedJournals, // 削除済み仕訳（電子帳簿保存法対応）
            updatedAt: new Date().toISOString()
        };

        if (fileSystem.isConnected) {
            const success = await fileSystem.saveData(currentFiscalYear, data);
            if (success) {
                setHasUnsavedChanges(false);
            }
            return success;
        } else {
            showAlert('APIに接続できません。データを保存できませんでした。', 'error');
            return false;
        }
    };

    // 自動保存（デバウンス）- 起動完了後かつ読み込み中でない場合のみ
    useEffect(() => {
        // 起動が完了していない、または読み込み中は保存しない
        if (startupStep !== 'done' || isLoading) {
            return;
        }

        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        setHasUnsavedChanges(true);

        saveTimeoutRef.current = setTimeout(() => {
            saveData();
        }, 2000);

        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [accounts, journals, deletedJournals, startupStep, isLoading]);

    // 年度変更時にデータを再読み込み（マスタがあればマスタを使用）
    useEffect(() => {
        if (startupStep === 'done') {
            loadYearData(currentFiscalYear);
        }
    }, [currentFiscalYear, fileSystem.isConnected, startupStep]);

    // 起動時に年度一覧とマスタを読み込み（年度一覧を優先、マスタはバックグラウンド）
    // user?.email を依存配列に含めてログイン後に再実行
    useEffect(() => {
        const effectiveEmail = activeBookEmail || user?.email;
        if (!effectiveEmail) {
            // 未ログイン: デフォルト年度だけセットしてローディング解除
            setIsLoadingYears(false);
            return;
        }

        setIsLoadingYears(true);

        // 年度一覧を先に取得（軽量・高速）
        const loadYears = async () => {
            try {
                // years は [{year, endMonth}, ...] 形式
                const years = await fileSystem.listYears();
                setFiscalYears(years);
                // 最新年度を選択
                if (years.length > 0) {
                    setSelectedStartupYear(years[0].year);
                }
            } catch (err) {
                console.error('Failed to list years:', err);
            } finally {
                setIsLoadingYears(false);
            }
        };

        // 設定をAPIから読み込み
        const loadSettings = async () => {
            try {
                const settings = await fileSystem.getSettings();
                if (settings) {
                    if (settings.detailSettings) {
                        setDetailSettings(settings.detailSettings);
                    }
                    if (settings.allocationSettings) {
                        setAllocationSettings(settings.allocationSettings);
                    }
                }
            } catch (err) {
                console.error('Failed to load settings:', err);
            }
        };

        // クレジットカードマスタを読み込み
        const loadCreditCards = async () => {
            try {
                const cards = await fileSystem.getCreditCards();
                if (cards) {
                    setCreditCards(cards);
                }
            } catch (err) {
                console.error('Failed to load credit cards:', err);
            }
        };

        // 年度一覧（init）を先に完了させ、その後設定を並行して読み込み
        // initで設定フォルダが作成されるため、順序が重要
        loadYears().then(() => {
            loadSettings();
            loadCreditCards();
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeBookEmail, user?.email]);

    // 設定変更時にAPIに保存
    useEffect(() => {
        if (startupStep !== 'done' || !user?.email) return;

        const saveSettingsToAPI = async () => {
            try {
                await fileSystem.saveSettings({
                    detailSettings,
                    allocationSettings
                });
            } catch (err) {
                console.error('Failed to save settings:', err);
            }
        };
        saveSettingsToAPI();
    }, [detailSettings, allocationSettings, startupStep, user?.email]);

    // 年度一覧がロードされたら、最新年度の決算月をデフォルトに設定
    useEffect(() => {
        if (fiscalYears.length > 0) {
            setNewEndMonth(fiscalYears[0].endMonth);
        }
    }, [fiscalYears]);

    // 新年度作成（endMonthは省略可能、省略時は前年度の決算月を引き継ぐ）
    const createNewFiscalYear = async (year, endMonth = null) => {
        if (fiscalYears.some(fy => fy.year === year)) {
            showAlert('この年度は既に存在します', 'error');
            return;
        }

        // 決算月を決定（指定なければ前年度を引き継ぐ、なければ3月）
        const isKojin = orgInfo && isIndividual(orgInfo.type);
        const fiscalEndMonth = isKojin ? 12 : (endMonth || (fiscalYears.length > 0 ? fiscalYears[0].endMonth : 3));

        // APIで年度フォルダを作成
        const success = await fileSystem.createYear(year, fiscalEndMonth);
        if (!success) {
            showAlert('年度の作成に失敗しました', 'error');
            return;
        }

        const newFiscalYear = { year, endMonth: fiscalEndMonth };
        const newYears = [...fiscalYears, newFiscalYear].sort((a, b) => b.year - a.year);
        setFiscalYears(newYears);

        // 前年度の勘定科目をコピー
        const prevYear = year - 1;
        let prevAccounts = DEFAULT_ACCOUNTS;

        if (fileSystem.isConnected) {
            const prevData = await fileSystem.loadData(prevYear);
            if (prevData && prevData.accounts) {
                prevAccounts = prevData.accounts;
            }
        }

        // 新年度のデータを保存
        const newData = {
            fiscalYear: year,
            accounts: prevAccounts,
            journals: [],
            updatedAt: new Date().toISOString()
        };

        if (fileSystem.isConnected) {
            await fileSystem.saveData(year, newData);
        }

        setCurrentFiscalYear(year);
        const yearLabel = isKojin ? '年' : '年度';
        showAlert(`${year}${yearLabel}を作成しました`);
    };

    // 勘定科目ごとの残高計算
    const calculateBalances = useMemo(() => {
        const balances = {};
        accounts.forEach(acc => {
            balances[acc.id] = { debit: 0, credit: 0 };
        });

        journals.forEach(journal => {
            journal.lines.forEach(line => {
                if (balances[line.accountId]) {
                    if (line.type === 'debit') {
                        balances[line.accountId].debit += line.amount;
                    } else {
                        balances[line.accountId].credit += line.amount;
                    }
                }
            });
        });

        return balances;
    }, [accounts, journals]);

    // 勘定科目の残高取得
    const getAccountBalance = (accountId) => {
        const account = accounts.find(a => a.id === accountId);
        if (!account) return 0;
        const balance = calculateBalances[accountId] || { debit: 0, credit: 0 };

        // 資産・費用は借方 - 貸方、負債・純資産・収益は貸方 - 借方
        if (inferAccountType(account) === ACCOUNT_TYPES.ASSET || inferAccountType(account) === ACCOUNT_TYPES.EXPENSE) {
            return balance.debit - balance.credit;
        } else {
            return balance.credit - balance.debit;
        }
    };

    // 科目変更ハンドラー（subject.csvに保存）
    const handleAccountsChange = async (newAccounts) => {
        setAccounts(newAccounts);
        try {
            const csvText = fileSystem.accountsToCSV(newAccounts);
            const at = localStorage.getItem('haniho_account_type') || 'houjin';
            const res = await fetch(`${import.meta.env.VITE_API_URL}?action=saveAccounts&type=${at}&user=${encodeURIComponent(user?.email)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'text/plain' },
                body: csvText
            });
            if (!res.ok) {
                showAlert('勘定科目の保存に失敗しました', 'error');
                return false;
            }
            return true;
        } catch {
            showAlert('勘定科目の保存に失敗しました', 'error');
            return false;
        }
    };

    // OCR処理
    const processOCR = async (file) => {
        console.log('processOCR called with file:', file);
        setIsOcrProcessing(true);
        try {
            const formData = new FormData();
            formData.append('receipt', file);

            console.log('Sending OCR request to:', `${import.meta.env.VITE_API_URL}?action=ocr`);
            const response = await fetch(`${import.meta.env.VITE_API_URL}?action=ocr`, {
                method: 'POST',
                body: formData
            });

            console.log('OCR response status:', response.status);
            if (!response.ok) {
                const errorText = await response.text();
                console.error('OCR error response:', errorText);
                throw new Error(`OCR処理に失敗しました: ${response.status}`);
            }

            const data = await response.json();
            console.log('OCR result:', data);

            // OCRデータを保存
            setOcrData({
                date: data.date || new Date().toISOString().split('T')[0],
                description: data.description || '',
                partner: data.merchant || '',
                debit_account: data.suggested_debit_account || '',
                debit_amount: data.amount ? String(data.amount) : '',
                debit_tax_type: data.suggested_debit_tax || '課税仕入10%',
                credit_account: data.suggested_credit_account || '',
                credit_amount: data.amount ? String(data.amount) : '',
                credit_tax_type: '対象外'
            });

            // 入力画面へ遷移
            console.log('Transitioning to entry screen');
            setMobileScreen('entry');
        } catch (err) {
            console.error('OCR error:', err);
            showAlert(`領収書の読み取りに失敗しました: ${err.message}`, 'error');
            setMobileScreen('list');
        } finally {
            setIsOcrProcessing(false);
        }
    };

    // モバイル画面ハンドラー
    const handleCameraCapture = (file) => {
        console.log('handleCameraCapture called with file:', file);
        if (!file) {
            console.error('No file provided to handleCameraCapture');
            showAlert('ファイルの取得に失敗しました', 'error');
            return;
        }
        processOCR(file);
    };

    const handleMobileEntrySubmit = async (formData) => {
        try {
            const newJournal = {
                id: Date.now(),
                date: formData.date,
                description: formData.description,
                partner: formData.partner,
                debit_account: formData.debit_account,
                debit_amount: formData.amount,
                debit_tax_type: formData.debit_tax_type,
                credit_account: formData.credit_account,
                credit_amount: formData.amount,
                credit_tax_type: formData.credit_tax_type,
                amount: formData.amount
            };

            const updatedJournals = [...journals, newJournal];
            setJournals(updatedJournals);
            setHasUnsavedChanges(true);

            // 自動保存
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
            saveTimeoutRef.current = setTimeout(async () => {
                await saveData();
                setHasUnsavedChanges(false);
            }, 1000);

            showAlert('仕訳を登録しました', 'success');
            setMobileScreen('list');
            setOcrData(null);
        } catch (err) {
            console.error('Failed to save journal:', err);
            showAlert('仕訳の登録に失敗しました', 'error');
        }
    };

    // ページレンダリング
    const renderPage = () => {
        const props = {
            accounts,
            setAccounts: handleAccountsChange,
            journals,
            setJournals,
            showAlert,
            detailSettings,
            setDetailSettings,
            allocationSettings,
            setAllocationSettings,
            calculateBalances,
            getAccountBalance,
            currentFiscalYear,
            fiscalYears,
            setFiscalYears,
            fileSystem,
            saveData,
            focusJournalId,
            setFocusJournalId,
            setCurrentPage: navigateTo,
            deletedJournals,
            setDeletedJournals,
            creditCards,
            setCreditCards,
            accountType: orgInfo && isIndividual(orgInfo.type) ? 'kojin' : 'houjin',
            orgType: orgInfo?.type,
            orgInfo,
            setOrgInfo,
            user,
            isBookOwner,
            activeBookEmail
        };

        if (isLoading) {
            return (
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p style={{color: '#666'}}>データを読み込んでいます...</p>
                </div>
            );
        }

        // モバイル画面のレンダリング
        if (isMobile && currentPage === 'mobile-journal-list') {
            if (isOcrProcessing) {
                return (
                    <div style={{
                        position: 'fixed',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#fff',
                        zIndex: 9999
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <div className="spinner"></div>
                            <p style={{ marginTop: '20px', color: '#666' }}>領収書を読み取り中...</p>
                        </div>
                    </div>
                );
            }

            switch (mobileScreen) {
                case 'list':
                    return (
                        <JournalList
                            journals={journals}
                            fiscalYear={currentFiscalYear}
                            onCameraClick={() => setMobileScreen('camera')}
                            onJournalClick={(journal) => {
                                // 仕訳編集機能（将来実装）
                                console.log('Edit journal:', journal);
                            }}
                        />
                    );
                case 'camera':
                    return (
                        <CameraScreen
                            onCapture={handleCameraCapture}
                            onClose={() => setMobileScreen('list')}
                        />
                    );
                case 'entry':
                    return (
                        <EntryForm
                            initialData={ocrData}
                            accounts={accounts}
                            onSubmit={handleMobileEntrySubmit}
                            onClose={() => {
                                setMobileScreen('list');
                                setOcrData(null);
                            }}
                            onReshoot={() => setMobileScreen('camera')}
                        />
                    );
                default:
                    return (
                        <JournalList
                            journals={journals}
                            fiscalYear={currentFiscalYear}
                            onCameraClick={() => setMobileScreen('camera')}
                        />
                    );
            }
        }

        switch (currentPage) {
            case 'dashboard':
                return <Dashboard {...props} />;
            case 'journals':
                return <JournalEntry {...props} />;
            case 'accounts':
                return <AccountMaster {...props} />;
            case 'ledger':
                return <GeneralLedger {...props} />;
            case 'trial-balance':
                return <TrialBalance {...props} />;
            case 'financial-statements':
                return <FinancialStatements {...props} />;
            case 'tax-summary':
                return <TaxSummary {...props} />;
            case 'fixed-assets':
                return <FixedAssets {...props} accountType={storedAccountType} />;
            case 'card-settlement':
                return <CardSettlement {...props} />;
            case 'closing-wizard':
                return <ClosingWizard {...props} accountType={storedAccountType} />;
            case 'etax':
                return <ETaxTransfer {...props} />;
            case 'deductions':
                // 所得控除は個人事業主専用
                if (!isIndividual(orgInfo?.type)) {
                    return <Dashboard {...props} />;
                }
                return <Deductions {...props} />;
            case 'data-management':
                return <DataManagement {...props} createNewFiscalYear={createNewFiscalYear} orgType={getOrgInfo()?.type} />;
            case 'help':
                return <Help {...props} />;
            default:
                return <Dashboard {...props} />;
        }
    };

    // 起動時：帳簿フォルダ選択処理
    const handleSelectBaseFolder = async () => {
        try {
            const handle = await fileSystem.selectBaseDirectory();
            if (handle) {
                // 年度一覧を取得（オブジェクト形式 [{year, endMonth}, ...]）
                const years = await fileSystem.listYears();
                if (years.length > 0) {
                    setFiscalYears(years);
                    setSelectedStartupYear(years[0].year); // 最新年度を選択
                } else {
                    // 年度フォルダがない場合は空のまま
                    setFiscalYears([]);
                    setSelectedStartupYear(getCurrentFiscalYear());
                }
                setStartupStep('year');
            }
        } catch (err) {
            showAlert(err.message, 'error');
        }
    };

    // 起動時：年度選択確定処理（この関数は現在使われていない）
    const handleSelectYear = async () => {
        setCurrentFiscalYear(selectedStartupYear);
        await loadYearData(selectedStartupYear);
        setStartupStep('done');
        const yearLabel = orgInfo && isIndividual(orgInfo.type) ? '年' : '年度';
        showAlert(`${selectedStartupYear}${yearLabel} を開きました`);
    };

    // 起動時：次年度追加（この関数は現在使われていない）
    const handleAddNextYear = async () => {
        const maxYear = fiscalYears.length > 0
            ? Math.max(...fiscalYears.map(fy => fy.year))
            : getCurrentFiscalYear();
        const nextYear = maxYear + 1;
        const endMonth = fiscalYears.length > 0 ? fiscalYears[0].endMonth : 3;
        const success = await fileSystem.createYear(nextYear, endMonth);
        if (success) {
            const newFy = { year: nextYear, endMonth };
            setFiscalYears(prev => [...prev, newFy].sort((a, b) => b.year - a.year));
            setSelectedStartupYear(nextYear);
        }
    };

    // 起動時モーダル - Step 1: フォルダ選択
    if (startupStep === 'folder' && !fileSystem.isConnected) {
        return (
            <div className="modal-overlay" style={{background: 'linear-gradient(135deg, #2c3e50 0%, #1a252f 100%)'}}>
                <div className="modal" style={{maxWidth: '600px', textAlign: 'center'}}>
                    <h2 style={{marginBottom: '20px', color: '#2c3e50'}}>
                        {orgInfo?.name || 'トイロ法人会計'}<br />会計システム
                    </h2>

                    <div style={{background: '#f8f9fa', padding: '20px', borderRadius: '8px', marginBottom: '20px', textAlign: 'left'}}>
                        <h3 style={{marginBottom: '12px', fontSize: '16px'}}>帳簿フォルダを選択してください</h3>
                        <p style={{color: '#666', fontSize: '14px', marginBottom: '12px'}}>
                            データを保存する「帳簿」フォルダを選択します。<br />
                            初回のみフォルダを選択すれば、次回からは年度を選ぶだけで開けます。
                        </p>
                        <div className="path-display">
                            {RECOMMENDED_PATH}
                        </div>
                        <p style={{color: '#888', fontSize: '13px', marginTop: '12px'}}>
                            ※ Chrome または Edge ブラウザが必要です
                        </p>
                    </div>

                    <div className="btn-group" style={{justifyContent: 'center'}}>
                        <button className="btn btn-primary" onClick={handleSelectBaseFolder} style={{padding: '12px 32px', fontSize: '16px'}}>
                            帳簿フォルダを選択
                        </button>
                    </div>

                    <div style={{marginTop: '20px'}}>
                        <button
                            className="btn btn-secondary"
                            onClick={() => setStartupStep('done')}
                            style={{fontSize: '13px'}}
                        >
                            フォルダを選択せずに続ける（一時保存モード）
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ログイン後、組織情報を読み込み中の場合はローディング表示
    if (user && isLoadingOrgInfo) {
        return (
            <div className="login-container">
                <div className="login-card">
                    <div style={{textAlign: 'center', padding: '40px'}}>
                        <div className="spinner" style={{margin: '0 auto 16px'}}></div>
                        <p style={{color: '#666'}}>読み込んでいます...</p>
                    </div>
                </div>
            </div>
        );
    }

    // ログイン後、組織情報が未設定の場合は設定画面を表示（初回のみ）
    if (user && !orgInfo) {
        // 組織設定用のステート（コンポーネント外で使うため関数コンポーネント化）
        const OrgSetupScreen = () => {
            const [step, setStep] = React.useState(1); // 1: 種別選択, 2: 名前入力 (3: subType for ippan-shadan)
            const [selectedType, setSelectedType] = React.useState(null);
            const [selectedSubType, setSelectedSubType] = React.useState('nonprofit');
            const [orgName, setOrgName] = React.useState('');
            const [canSubmit, setCanSubmit] = React.useState(true); // IME変換後のsubmit防止

            const handleSelectType = (type) => {
                setSelectedType(type);
                if (type === ORG_TYPES.IPPAN_SHADAN) {
                    setStep(3); // subType選択へ
                } else {
                    setStep(2);
                }
            };

            const handleComplete = async () => {
                // IME変換直後は実行しない
                if (!canSubmit) return;
                if (!orgName.trim()) {
                    alert(isIndividual(selectedType) ? '氏名または屋号を入力してください' : '法人名を入力してください');
                    return;
                }

                const newOrgInfo = {
                    type: selectedType,
                    name: orgName.trim(),
                    ...(selectedType === ORG_TYPES.IPPAN_SHADAN ? { subType: selectedSubType } : {})
                };

                try {
                    // APIに保存（Google Drive）
                    await saveOrgInfo(newOrgInfo, activeBookEmail || user?.email);
                    // localStorageにも保存
                    localStorage.setItem('toiro_org_info', JSON.stringify(newOrgInfo));
                    localStorage.setItem('haniho_account_type', isIndividual(selectedType) ? 'kojin' : 'houjin');
                    // stateを更新
                    setOrgInfo(newOrgInfo);
                    // リロードせずに画面遷移
                    window.location.reload();
                } catch (err) {
                    console.error('Failed to save org info:', err);
                    alert('組織情報の保存に失敗しました');
                }
            };

            const orgTypeGroups = [
                {
                    label: '個人',
                    color: '#3498db',
                    types: [
                        { type: ORG_TYPES.KOJIN, label: '個人事業主', desc: '青色申告' }
                    ]
                },
                {
                    label: '法人（営利・非営利）',
                    color: '#2c3e50',
                    types: [
                        { type: ORG_TYPES.IPPAN_SHADAN, label: '一般社団法人', desc: '' }
                    ]
                }
            ];

            if (step === 1) {
                return (
                    <div className="login-container">
                        <div className="login-card" style={{maxWidth: '600px', position: 'relative'}}>
                            <button onClick={handleLogout} style={{position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: '13px'}}>
                                ログアウト
                            </button>
                            <h1 style={{marginBottom: '8px'}}>トイロ会計</h1>
                            <p style={{color: '#666', marginBottom: '32px'}}>シンプルな会計システム</p>
                            <h2 style={{fontSize: '18px', marginBottom: '24px', color: '#333'}}>事業形態を選択してください</h2>

                            <div style={{display: 'flex', flexDirection: 'column', gap: '24px'}}>
                                {orgTypeGroups.map(group => (
                                    <div key={group.label}>
                                        <h3 style={{fontSize: '14px', color: group.color, marginBottom: '12px', fontWeight: 'bold', textAlign: 'center'}}>
                                            {group.label}
                                        </h3>
                                        <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center'}}>
                                            {group.types.map(item => (
                                                <button
                                                    key={item.type}
                                                    onClick={() => handleSelectType(item.type)}
                                                    style={{
                                                        padding: '16px 24px',
                                                        fontSize: '15px',
                                                        background: 'white',
                                                        color: '#333',
                                                        border: `2px solid ${group.color}`,
                                                        borderRadius: '8px',
                                                        cursor: 'pointer',
                                                        minWidth: '140px',
                                                        textAlign: 'left',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onMouseOver={(e) => {
                                                        e.target.style.background = group.color;
                                                        e.target.style.color = 'white';
                                                    }}
                                                    onMouseOut={(e) => {
                                                        e.target.style.background = 'white';
                                                        e.target.style.color = '#333';
                                                    }}
                                                >
                                                    <div style={{fontWeight: 'bold'}}>{item.label}</div>
                                                    {item.desc && <div style={{fontSize: '12px', marginTop: '4px', opacity: 0.8}}>{item.desc}</div>}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>

                        </div>
                    </div>
                );
            }

            // Step 3: 一般社団法人のsubType選択
            if (step === 3) {
                return (
                    <div className="login-container">
                        <div className="login-card" style={{maxWidth: '500px', position: 'relative'}}>
                            <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setStep(1); }}
                                style={{ position: 'absolute', top: '16px', left: '16px', background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '14px', zIndex: 10 }}
                            >
                                ← 戻る
                            </button>
                            <button onClick={handleLogout} style={{position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: '13px'}}>
                                ログアウト
                            </button>
                            <h1 style={{marginBottom: '8px'}}>トイロ会計</h1>
                            <p style={{color: '#666', marginBottom: '8px'}}>
                                <span style={{ background: '#2c3e50', color: 'white', padding: '4px 12px', borderRadius: '4px', fontSize: '13px' }}>一般社団法人</span>
                            </p>
                            <h2 style={{fontSize: '18px', marginTop: '24px', marginBottom: '24px', color: '#333'}}>法人の種類を選択</h2>

                            <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                                {[
                                    { value: 'nonprofit', label: '非営利型', desc: '収益事業のみ課税・正味財産増減計算書・区分経理あり' },
                                    { value: 'forprofit', label: '普通型（営利型）', desc: '全所得課税・損益計算書でも可・区分経理なし' }
                                ].map(item => (
                                    <button
                                        key={item.value}
                                        onClick={() => { setSelectedSubType(item.value); setStep(2); }}
                                        style={{
                                            padding: '16px 20px', fontSize: '15px', background: selectedSubType === item.value ? '#2c3e50' : 'white',
                                            color: selectedSubType === item.value ? 'white' : '#333', border: '2px solid #2c3e50',
                                            borderRadius: '8px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s'
                                        }}
                                    >
                                        <div style={{fontWeight: 'bold'}}>{item.label}</div>
                                        <div style={{fontSize: '12px', marginTop: '4px', opacity: 0.8}}>{item.desc}</div>
                                    </button>
                                ))}
                            </div>

                            <p style={{fontSize: '12px', color: '#999', marginTop: '16px'}}>
                                ※ 後から「データ管理」→「組織情報」で変更できます
                            </p>
                        </div>
                    </div>
                );
            }

            // Step 2: 名前入力
            const nameLabel = isIndividual(selectedType) ? '氏名または屋号' : '法人名';
            const namePlaceholder = isIndividual(selectedType) ? '例: 山田太郎 / ○○事務所' : '例: 株式会社○○';
            const typeLabel = ORG_TYPE_LABELS[selectedType];

            return (
                <div className="login-container">
                    <div className="login-card" style={{maxWidth: '500px', position: 'relative'}}>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setStep(1);
                            }}
                            style={{
                                position: 'absolute',
                                top: '16px',
                                left: '16px',
                                background: 'none',
                                border: 'none',
                                color: '#999',
                                cursor: 'pointer',
                                fontSize: '14px',
                                zIndex: 10
                            }}
                        >
                            ← 戻る
                        </button>
                        <h1 style={{marginBottom: '8px'}}>トイロ会計</h1>
                        <p style={{color: '#666', marginBottom: '8px'}}>
                            <span style={{
                                background: isIndividual(selectedType) ? '#27ae60' : isForProfit(selectedType) ? '#3498db' : '#2c3e50',
                                color: 'white',
                                padding: '4px 12px',
                                borderRadius: '4px',
                                fontSize: '13px'
                            }}>{typeLabel}</span>
                        </p>
                        <h2 style={{fontSize: '18px', marginTop: '24px', marginBottom: '24px', color: '#333'}}>{nameLabel}を入力</h2>
                        <p style={{fontSize: '10px', color: '#ccc'}}>v2</p>

                        <div style={{marginBottom: '24px'}}>
                            <input
                                type="text"
                                value={orgName}
                                onChange={(e) => setOrgName(e.target.value)}
                                onCompositionStart={() => setCanSubmit(false)}
                                onCompositionEnd={() => setTimeout(() => setCanSubmit(true), 300)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); } }}
                                placeholder={namePlaceholder}
                                style={{
                                    width: '100%',
                                    padding: '16px',
                                    fontSize: '18px',
                                    border: '2px solid #ddd',
                                    borderRadius: '8px',
                                    boxSizing: 'border-box'
                                }}
                                autoFocus
                            />
                            <p style={{fontSize: '13px', color: '#888', marginTop: '8px'}}>
                                ※ 帳票や画面表示に使用されます。後から変更可能です。
                            </p>
                        </div>

                        <button
                            type="button"
                            onClick={handleComplete}
                            style={{
                                width: '100%',
                                padding: '16px',
                                fontSize: '16px',
                                background: isIndividual(selectedType) ? '#27ae60' : isForProfit(selectedType) ? '#3498db' : '#2c3e50',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: 'bold'
                            }}
                        >
                            設定を完了してログインへ
                        </button>
                    </div>
                </div>
            );
        };

        return <OrgSetupScreen />;
    }

    // ログイン処理中はスピナーを表示
    if (isLoginProcessing) {
        return (
            <div className="login-container">
                <div className="login-card">
                    <div style={{textAlign: 'center', padding: '40px'}}>
                        <div className="spinner" style={{margin: '0 auto 16px'}}></div>
                        <p style={{color: '#666'}}>ログイン中...</p>
                    </div>
                </div>
            </div>
        );
    }

    // ログインしていない場合はログイン画面を表示
    if (!user) {
        const orgInfo = getOrgInfo();
        const orgTypeLabel = orgInfo ? ORG_TYPE_LABELS[orgInfo.type] : '';
        const serviceName = isIndividual(orgInfo?.type) ? 'トイロ青色申告' : 'トイロ会計';

        return (
            <div className="login-container">
                <div className="login-card">
                    <button
                        onClick={() => {
                            window.location.reload();
                        }}
                        style={{
                            position: 'absolute',
                            top: '16px',
                            left: '16px',
                            background: 'none',
                            border: 'none',
                            color: '#999',
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        ← 戻る
                    </button>
                    <h1>{serviceName}</h1>
                    <div style={{ marginBottom: '20px', color: '#666' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '18px', color: '#333' }}>
                            {orgInfo?.name || '未設定'}
                        </div>
                        <div style={{ fontSize: '14px', marginTop: '4px' }}>
                            {orgTypeLabel}
                        </div>
                    </div>
                    <p>{inviteToken ? '招待を受けるにはGoogleアカウントでログインしてください' : 'Googleアカウントでログインしてください'}</p>
                    <button className="login-btn" onClick={handleGoogleLogin}>
                        <svg viewBox="0 0 24 24" fill="white">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Googleでログイン
                    </button>

                </div>
            </div>
        );
    }

    // 帳簿選択画面（複数の帳簿にアクセス可能な場合）
    if (user && showBookSelector && availableBooks.length > 1) {
        return (
            <div className="modal-overlay" style={{background: 'linear-gradient(135deg, #2c3e50 0%, #1a252f 100%)'}}>
                <div className="modal" style={{maxWidth: '500px', textAlign: 'center', position: 'relative'}}>
                    <button onClick={handleLogout} style={{position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: '13px'}}>
                        ログアウト
                    </button>
                    <h2 style={{marginBottom: '20px', color: '#2c3e50'}}>帳簿を選択</h2>
                    <p style={{color: '#666', marginBottom: '20px'}}>アクセス可能な帳簿が複数あります。</p>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                        {availableBooks.map((book, i) => (
                            <button
                                key={i}
                                onClick={() => handleBookSelect(book)}
                                style={{
                                    padding: '16px 20px',
                                    border: '2px solid #e0e0e0',
                                    borderRadius: '8px',
                                    background: '#fff',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    fontSize: '16px',
                                    transition: 'border-color 0.2s'
                                }}
                                onMouseEnter={e => e.target.style.borderColor = '#3498db'}
                                onMouseLeave={e => e.target.style.borderColor = '#e0e0e0'}
                            >
                                <div style={{fontWeight: '600', color: '#333'}}>{book.name}</div>
                                <div style={{fontSize: '13px', color: '#999', marginTop: '4px'}}>
                                    {book.role === 'owner' ? 'オーナー' : '編集メンバー'}
                                    {book.orgType && ` · ${ORG_TYPE_LABELS[book.orgType] || book.orgType}`}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // 年度選択画面（ログイン後、年度未選択の場合）
    const currentOrgInfo = getOrgInfo();
    const orgType = currentOrgInfo?.type;
    const isKojinMode = isIndividual(orgType);
    const serviceName = isKojinMode ? 'トイロ青色申告' : 'トイロ会計';
    const yearLabel = isKojinMode ? '年' : '年度';

    // 年度の期間表示を取得（fiscalYearオブジェクトから）
    const getYearPeriodFromObj = (fiscalYear) => {
        const { year, endMonth } = fiscalYear;
        if (isKojinMode) {
            return `${year}年度（${year}/1〜${year}/12）`;
        }
        const startMonth = endMonth === 12 ? 1 : endMonth + 1;
        const startYear = endMonth === 12 ? year : year;
        const endYear = endMonth === 12 ? year : year + 1;
        return `${year}年度（${startYear}/${startMonth}〜${endYear}/${endMonth}）`;
    };

    if (startupStep === 'year') {
        const handleSelectYearFromList = (fiscalYear) => {
            // isLoadingをtrueにしてからstartupStepを'done'にする
            // （同一renderにバッチされるため、保存useEffectがisLoading=trueを見てスキップし、
            //   空journals[]でjournals.csvを上書きするレースコンディションを防ぐ）
            setIsLoading(true);
            setCurrentFiscalYear(fiscalYear.year);
            setStartupStep('done');
        };

        const handleCreateNewYear = async () => {
            // 既存チェック
            if (fiscalYears.some(fy => fy.year === newYearInput)) {
                alert('この年度は既に存在します');
                return;
            }

            setIsCreating(true);
            const endMonth = isKojinMode ? 12 : newEndMonth;
            const success = await fileSystem.createYear(newYearInput, endMonth);

            if (success) {
                const newFiscalYear = { year: newYearInput, endMonth };
                setFiscalYears(prev => [...prev, newFiscalYear].sort((a, b) => b.year - a.year));
                // 法人の場合、決算月を保存して次回のデフォルトにする
                if (!isKojinMode) {
                    localStorage.setItem('haniho_last_end_month', String(endMonth));
                }
                handleSelectYearFromList(newFiscalYear);
                setShowNewYearModal(false);
            } else {
                alert('年度の作成に失敗しました');
            }
            setIsCreating(false);
        };

        return (
            <div className="login-container">
                <div className="login-card" style={{maxWidth: '400px'}}>
                    <h1 style={{marginBottom: '8px'}}>{serviceName}</h1>
                    <p style={{color: '#666', marginBottom: '8px'}}>{getOrgInfo()?.name || user.name || user.email}</p>
                    <h2 style={{fontSize: '18px', marginBottom: '24px', color: '#333'}}>{yearLabel}を選択</h2>

                    {isLoadingYears ? (
                        <div style={{textAlign: 'center', padding: '40px'}}>
                            <div className="spinner" style={{margin: '0 auto 16px'}}></div>
                            <p style={{color: '#666'}}>{yearLabel}を読み込んでいます...</p>
                        </div>
                    ) : fiscalYears.length > 0 ? (
                        // データあり：去年・今年・来年のみ表示
                        (() => {
                            const calYear = new Date().getFullYear();
                            const visibleYears = fiscalYears.filter(fy =>
                                fy.year >= calYear - 1 && fy.year <= calYear + 1
                            );
                            return (
                                <div style={{display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px'}}>
                                    {visibleYears.map(fy => (
                                        <button
                                            key={fy.year}
                                            onClick={() => handleSelectYearFromList(fy)}
                                            style={{
                                                padding: '16px',
                                                fontSize: '16px',
                                                background: '#f8f9fa',
                                                border: '1px solid #dee2e6',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                textAlign: 'left'
                                            }}
                                        >
                                            {getYearPeriodFromObj(fy)}
                                        </button>
                                    ))}
                                </div>
                            );
                        })()
                    ) : (
                        // データなし：新規作成ボタンのみ
                        !isLoadingYears && (
                            <button
                                onClick={() => setShowNewYearModal(true)}
                                style={{
                                    padding: '12px 24px',
                                    fontSize: '14px',
                                    background: isKojinMode ? '#42a5f5' : '#2c3e50',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    width: '100%',
                                    marginBottom: '24px'
                                }}
                            >
                                + 新しい{yearLabel}を作成
                            </button>
                        )
                    )}

                    <button
                        onClick={handleLogout}
                        style={{
                            marginTop: '24px',
                            background: 'none',
                            border: 'none',
                            color: '#999',
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        ログアウト
                    </button>
                </div>

                {/* 新規年度作成モーダル */}
                {showNewYearModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}>
                        <div style={{
                            background: 'white',
                            padding: '32px',
                            borderRadius: '12px',
                            minWidth: '320px'
                        }}>
                            <h3 style={{marginBottom: '24px'}}>新しい{yearLabel}を作成</h3>

                            <div style={{marginBottom: '16px'}}>
                                <label style={{display: 'block', marginBottom: '8px', fontWeight: 'bold'}}>{yearLabel}</label>
                                <select
                                    value={newYearInput}
                                    onChange={(e) => setNewYearInput(parseInt(e.target.value))}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        fontSize: '16px',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '8px'
                                    }}
                                >
                                    {Array.from({length: 10}, (_, i) => new Date().getFullYear() - 5 + i).map(y => (
                                        <option key={y} value={y}>{y}{isKojinMode ? '年' : '年度'}</option>
                                    ))}
                                </select>
                            </div>

                            {!isKojinMode && (
                                <div style={{marginBottom: '24px'}}>
                                    <label style={{display: 'block', marginBottom: '8px', fontWeight: 'bold'}}>決算月</label>
                                    <select
                                        value={newEndMonth}
                                        onChange={(e) => setNewEndMonth(parseInt(e.target.value))}
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            fontSize: '16px',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '8px'
                                        }}
                                    >
                                        {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                                            <option key={m} value={m}>{m}月</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div style={{display: 'flex', gap: '12px'}}>
                                <button
                                    onClick={() => setShowNewYearModal(false)}
                                    disabled={isCreating}
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        fontSize: '14px',
                                        background: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '8px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    キャンセル
                                </button>
                                <button
                                    onClick={handleCreateNewYear}
                                    disabled={isCreating}
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        fontSize: '14px',
                                        background: isKojinMode ? '#27ae60' : '#2c3e50',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        opacity: isCreating ? 0.7 : 1
                                    }}
                                >
                                    {isCreating ? '作成中...' : '作成'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="app-container">
            {/* 新規科目追加通知バナー */}
            {newAccountsAlert && (
                <div
                    className="new-accounts-banner"
                    onClick={() => { setNewAccountsAlert(null); navigateTo('accounts'); }}
                >
                    <span className="message">
                        {newAccountsAlert.count}件の新しい勘定科目が追加されました。編集してください。
                        （{newAccountsAlert.names.slice(0, 3).join('、')}{newAccountsAlert.names.length > 3 ? '...' : ''}）
                    </span>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <span className="action">科目マスタを編集</span>
                        <button
                            className="close-btn"
                            onClick={(e) => { e.stopPropagation(); setNewAccountsAlert(null); }}
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}

            {/* Mobile Header with Hamburger Menu - Hide on mobile screens */}
            {!(isMobile && currentPage === 'mobile-journal-list') && (
                <div className="mobile-header">
                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            padding: '8px'
                        }}
                    >
                        ☰
                    </button>
                    <h2 style={{ margin: 0, fontSize: '18px' }}>トイロ会計</h2>
                    <div style={{ width: '40px' }}></div> {/* Spacer for centering */}
                </div>
            )}

            {/* Sidebar with mobile toggle - Hide on mobile screens */}
            {!(isMobile && currentPage === 'mobile-journal-list') && (
                <>
                    <div className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
                        <Sidebar
                            currentPage={currentPage}
                            setCurrentPage={(page) => {
                                navigateTo(page);
                                setMobileMenuOpen(false);
                            }}
                            currentFiscalYear={currentFiscalYear}
                            setCurrentFiscalYear={setCurrentFiscalYear}
                            fiscalYears={fiscalYears}
                            fileSystem={fileSystem}
                            hasUnsavedChanges={hasUnsavedChanges}
                            user={user}
                            onLogout={handleLogout}
                            accountType={storedAccountType}
                            orgType={getOrgInfo()?.type}
                            onCreateYear={createNewFiscalYear}
                        />
                    </div>

                    {/* Mobile Overlay */}
                    {mobileMenuOpen && (
                        <div
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'rgba(0,0,0,0.5)',
                                zIndex: 998
                            }}
                            onClick={() => setMobileMenuOpen(false)}
                        />
                    )}
                </>
            )}

            {/* Alert for mobile - Show on top */}
            {isMobile && currentPage === 'mobile-journal-list' && alertState && (
                <div style={{
                    position: 'fixed',
                    top: '20px',
                    left: '16px',
                    right: '16px',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    background: alertState.type === 'success' ? '#27ae60' : '#e74c3c',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: '500',
                    zIndex: 10000,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                }}>
                    {alertState.message}
                </div>
            )}

            {/* Main content - Full screen for mobile */}
            {isMobile && currentPage === 'mobile-journal-list' ? (
                renderPage()
            ) : (
                <main className="main-content" style={newAccountsAlert ? {marginTop: '48px'} : {}}>
                    {alertState && (
                        <div className={`alert alert-${alertState.type}`}>
                            {alertState.message}
                        </div>
                    )}
                    {!fileSystem.isConnected && (
                        <div className="alert alert-warning">
                            保存先フォルダが未設定です。「データ管理」から保存先を設定してください。現在はブラウザ内に一時保存されています。
                        </div>
                    )}
                    {renderPage()}
                </main>
            )}
        </div>
    );
};

export default App;
